import type { InvestmentMinInfo } from "@eu.mdotm.api/sphere";
import { typedUrlForRoute } from "$root/components/PlatformRouter/RoutesDef";
import { getCurrentUser } from "$root/functional-areas/user";
import { type ButtonAsyncFn, Button } from "@mdotm/shadcn/mdotm/components";
import {
	ActionText,
	AutoTooltip,
	Column,
	Dialog,
	DialogFooter,
	type DialogProps,
	Icon,
	Row,
	Text,
	TooltipContent,
} from "@mdotm/shadcn/legacy/components";
import { adaptAnimatedNodeProvider, spawn } from "@mdotm/shadcn/legacy/react-extensions";
import { ForEach } from "@mdotm/utils/react-extra";
import { getPortfolioStudioUrl } from "$root/pages/PortfoliosStudio/usePortfolioStudioRedirect";

type InstrumentCustomisationDeleteDialogType = "delete-column" | "delete-tag";

type SpawnInstrumentCustomisationDeleteDialogProps = {
	type: InstrumentCustomisationDeleteDialogType;
	investmentsUsedIn: Array<[string, InvestmentMinInfo[]]>;
	onContinue(): Promise<void>;
};

export async function spawnInstrumentCustomisationDeleteDialog(params: SpawnInstrumentCustomisationDeleteDialogProps) {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ resolve, show, onHidden }) => (
			<DeleteInstrumentCustomizationDialog
				{...params}
				show={show}
				onCancel={resolve}
				onContinue={() => {
					params.onContinue();
					resolve();
				}}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
			/>
		)),
	).promise;
}

export type DeleteInstrumentCustomizationDialogParams = {
	type: InstrumentCustomisationDeleteDialogType;
	investmentsUsedIn: Array<[string, InvestmentMinInfo[]]>;
	onContinue?: ButtonAsyncFn<void>;
};

const headerTitles = {
	"delete-column": (canDelete: boolean) => {
		return canDelete ? "Are you sure you want to delete this column?" : "Column will not be deleted";
	},
	"delete-tag": (canDelete: boolean) => {
		return canDelete ? "Are you sure you want to delete this tag?" : "Tag will not be deleted";
	},
} satisfies Record<InstrumentCustomisationDeleteDialogType, (canDelete: boolean) => string>;

export function DeleteInstrumentCustomizationDialog({
	show,
	investmentsUsedIn,
	type,
	onContinue,
	onCancel,
	onAnimationStateChange,
}: Omit<DeleteInstrumentCustomizationDialogParams, "onContinue"> & {
	onContinue: ButtonAsyncFn<void>;
	onCancel(): void;
} & Pick<DialogProps, "show" | "onAnimationStateChange">): React.JSX.Element {
	const myInvestments = investmentsUsedIn.find(([investment]) => investment === getCurrentUser().email)?.[1];
	const otherUsers = investmentsUsedIn
		.filter((investment) => investment.at(0) !== getCurrentUser().email)
		.map(([investment]) => investment);

	return (
		<Dialog
			size="large"
			show={show}
			onClose={onCancel}
			onAnimationStateChange={onAnimationStateChange}
			header={headerTitles[type](investmentsUsedIn.length === 0)}
			footer={
				<DialogFooter
					primaryAction={
						<Button
							palette="primary"
							size="small"
							data-qualifier="DeleteInstrumentCustomizationDialog/ConfirmButton"
							onClickAsync={onContinue}
						>
							{investmentsUsedIn.length > 0 ? "Confirm" : "Delete"}
						</Button>
					}
					neutralAction={
						<Button
							palette="tertiary"
							size="small"
							type="button"
							onClick={onCancel}
							data-qualifier="DeleteInstrumentCustomizationDialog/CancelButton"
						>
							Cancel
						</Button>
					}
				/>
			}
		>
			{investmentsUsedIn.length > 0
				? [
						myInvestments && (
							<Row flexGrow={0} className={{ "mb-4": otherUsers.length > 0 }}>
								<Column flexGrow={1}>
									<li className="list-disc ml-4"></li>
								</Column>
								<Column flexGrow={1}>
									<Row gap={4} alignItems="center" flexGrow={0}>
										<Text type="Body/M/Book">{`${myInvestments.length}${myInvestments.length > 1 ? " Portfolios are" : " Portfolio is"} currently using this ${type === "delete-column" ? "column" : "tag"} in one or more portfolio constraints. Please update the affected constraints before proceeding.`}</Text>
									</Row>
									<Row gap={4} alignItems="center" flexGrow={0}>
										<ActionText
											href={
												myInvestments.length === 1
													? typedUrlForRoute("PortfolioDetails", { portfolioUid: myInvestments.at(0)?.uuid ?? "" })
													: getPortfolioStudioUrl(
															myInvestments.flatMap((investment) => (investment.uuid ? [investment.uuid] : [])),
														)
											}
											target="_blank"
											rel="opener"
										>
											Open this selection in a new tab
										</ActionText>
										<Icon icon="Expand" />
									</Row>
								</Column>
							</Row>
						),
						otherUsers.length > 0 && (
							<Row flexGrow={0}>
								<Column flexGrow={1}>
									<li className="list-disc ml-4"></li>
								</Column>
								<Column flexGrow={1}>
									<Row gap={4} alignItems="center" flexGrow={0}>
										<AutoTooltip
											trigger={({ innerRef }) => (
												<Text type="Body/M/Book">
													Other{" "}
													<Text innerRef={innerRef} type="Body/M/Book" className="underline underline-offset-3">
														users
													</Text>{" "}
													are currently using this {type === "delete-column" ? "column" : "tag"} in one or more
													portfolio constraints. Please update the affected constraints before proceeding.
												</Text>
											)}
										>
											<TooltipContent>
												<ForEach collection={otherUsers}>{(item) => <p>{item}</p>}</ForEach>
											</TooltipContent>
										</AutoTooltip>
									</Row>
								</Column>
							</Row>
						),
					]
				: "Please be aware that this action cannot be undone."}
		</Dialog>
	);
}
